import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Filter, FileDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  exportMissedStandardsPDF,
  exportMissedStandardsExcel,
} from '@/hooks/queries/useMissedStandards';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/AuthProvider';

interface MissedStandardsFiltersProps {
  customDateInput: string;
  departmentFilter: string;
  departments: string[];
  onDateChange: (date: string) => void;
  onDepartmentChange: (dept: string) => void;
  onClearFilters: () => void;
  assessmentId: string;
  filteredCount: number;
}

export function MissedStandardsFilters({
  customDateInput,
  departmentFilter,
  departments,
  onDateChange,
  onDepartmentChange,
  onClearFilters,
  assessmentId,
  filteredCount,
}: MissedStandardsFiltersProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const { toast } = useToast();
  const { hasRole } = useAuth();
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isExportingExcel, setIsExportingExcel] = useState(false);
  
  // Hide export buttons for auditor role
  const canExport = !hasRole('auditor');

  const buildFilters = () => {
    const filters: { date?: string; department_id?: number } = {};
    if (customDateInput) filters.date = customDateInput;
    return filters;
  };

  const handleExportPDF = async () => {
    try {
      setIsExportingPDF(true);
      await exportMissedStandardsPDF(assessmentId, buildFilters());

      toast({
        title: t('missedStandards.filters.exportSuccess', 'Export Successful'),
        description: t(
          'missedStandards.filters.exportSuccessDescription',
          'PDF has been downloaded successfully'
        ),
      });
    } catch (error) {
      toast({
        title: t('missedStandards.filters.exportError', 'Export Failed'),
        description: t(
          'missedStandards.filters.exportErrorDescription',
          'Failed to generate PDF report'
        ),
        variant: 'destructive',
      });
    } finally {
      setIsExportingPDF(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setIsExportingExcel(true);
      await exportMissedStandardsExcel(assessmentId, buildFilters());

      toast({
        title: t('missedStandards.filters.exportSuccess', 'Export Successful'),
        description: t(
          'missedStandards.filters.exportExcelSuccessDescription',
          'Excel has been downloaded successfully'
        ),
      });
    } catch (error) {
      toast({
        title: t('missedStandards.filters.exportError', 'Export Failed'),
        description: t(
          'missedStandards.filters.exportExcelErrorDescription',
          'Failed to generate Excel report'
        ),
        variant: 'destructive',
      });
    } finally {
      setIsExportingExcel(false);
    }
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <div
          className={cn(
            'flex items-center justify-between',
            isRTL ? 'flex-row-reverse' : 'flex-row'
          )}
        >
          <div>
            <CardTitle
              className={cn(
                'flex items-center gap-2',
                isRTL ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <Filter className="w-5 h-5" />
              {t('missedStandards.filters.title')}
            </CardTitle>
            <CardDescription>
              {t('missedStandards.filters.description')}
            </CardDescription>
          </div>
          <div className="text-sm text-muted-foreground">
            {t('missedStandards.filters.standardsFound', {
              count: filteredCount,
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label
                htmlFor="custom-date-input"
                className={cn(isRTL && 'text-right')}
              >
                {t('missedStandards.filters.filterByDate')}
              </Label>
              <Input
                id="custom-date-input"
                type="date"
                value={customDateInput}
                onChange={e => onDateChange(e.target.value)}
                className={cn(isRTL && 'text-right')}
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="department-filter"
                className={cn(isRTL && 'text-right')}
              >
                {t('missedStandards.filters.filterByDepartment')}
              </Label>
              <Select
                value={departmentFilter}
                onValueChange={onDepartmentChange}
              >
                <SelectTrigger
                  id="department-filter"
                  className={cn(isRTL && 'text-right')}
                >
                  <SelectValue
                    placeholder={t('missedStandards.filters.allDepartments')}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all-departments">
                    {t('missedStandards.filters.allDepartments')}
                  </SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            {canExport && (
              <>
                <Button
                  variant="default"
                  onClick={handleExportPDF}
                  disabled={isExportingPDF || isExportingExcel}
                  className={cn(
                    'flex-1 h-12 sm:h-10',
                    isRTL ? 'flex-row-reverse' : 'flex-row'
                  )}
                >
                  <FileDown className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                  {isExportingPDF
                    ? t('common.loading', 'Loading...')
                    : t('missedStandards.filters.exportPDF')}
                </Button>
                <Button
                  variant="default"
                  onClick={handleExportExcel}
                  disabled={isExportingPDF || isExportingExcel}
                  className={cn(
                    'flex-1 h-12 sm:h-10 bg-green-600 hover:bg-green-700',
                    isRTL ? 'flex-row-reverse' : 'flex-row'
                  )}
                >
                  <FileDown className={cn('h-4 w-4', isRTL ? 'ml-2' : 'mr-2')} />
                  {isExportingExcel
                    ? t('common.loading', 'Loading...')
                    : t('missedStandards.filters.exportExcel')}
                </Button>
              </>
            )}
            <Button
              variant="outline"
              onClick={onClearFilters}
              disabled={isExportingPDF || isExportingExcel}
              className="flex-1 h-12 sm:h-10"
            >
              {t('missedStandards.filters.clearFilters')}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
